import { store } from '../redux/store'

/**
 * The single chokepoint through which a session may be torn down.
 *
 * WHY THIS EXISTS (CAPA F274, 2026-08-06):
 * F131 made the INACTIVITY timer recording-aware — an in-progress AI-Scribe encounter is
 * an *attended* session, and HIPAA auto-logoff targets unattended workstations, so the
 * idle path refuses to warn or log out mid-encounter. But the idle timer is not the only
 * thing that ends a session. `httpInterceptor` tears one down on a failed token renewal
 * and on an unrecoverable 401, and neither had ever heard of a recording:
 * `grep isRecording src/utils/httpInterceptor.tsx` returned nothing.
 *
 * So a clinician talking through an encounter — no mouse, no keyboard, token quietly
 * rotating underneath them — could lose the whole visit to a credential race that the
 * idle path was specifically written to protect them from. `performLogout` registers a
 * `beforeunload` that clears localStorage and sessionStorage and navigates away, which
 * unmounts the recorder and ends the capture.
 *
 * THE PATTERN (third occurrence): a control implemented on ONE path while other paths
 * reach the same outcome unguarded. 2026-08-03 — three logout paths bypassed the
 * countdown. F273 — the countdown was mounted inside one auth-flag branch. Here —
 * recording-awareness lived in the idle timer rather than at the place that actually ends
 * sessions. The fix each time is the same shape: one chokepoint every caller goes
 * through, never a guard re-implemented per call site.
 *
 * WHAT DEFERRAL BUYS: the credential is already dead and cannot be revived by waiting.
 * What waiting protects is the AUDIO. Not navigating away keeps the recorder mounted, so
 * capture continues into memory and IndexedDB; the clinician finishes the encounter, and
 * the upload either succeeds against a renewed token or the IndexedDB draft is recovered
 * after re-login. Navigating away immediately guarantees the loss; deferring makes it
 * recoverable.
 */

/** A pending teardown, held until the encounter finishes. */
let pendingTeardown: (() => void | Promise<void>) | null = null
let pendingReason: string = 'unspecified'
let unsubscribe: (() => void) | null = null

/**
 * Is clinical audio currently at risk from a navigation?
 *
 * Read from the store AT CALL TIME rather than from a captured selector value. The axios
 * interceptor's closure only refreshes when its registering effect re-runs, so a value
 * captured there goes stale mid-flight and would report "not recording" during a live
 * encounter — the same staleness class that made the backend-JWE cache serve a dead token.
 *
 * `isProcessing` is deliberately EXCLUDED: by then the audio has reached the server and a
 * logout costs a job-status poll, not a visit. The three included states all mean bytes
 * exist only on this machine.
 */
export const isEncounterAudioAtRisk = (): boolean => {
  const recording = (store.getState() as any)?.recording
  if (!recording) return false
  return !!(recording.isRecording || recording.isPaused || recording.isUploading)
}

/**
 * Run `teardown`, unless clinical audio is at risk — in which case hold it until the
 * encounter finishes, then run it.
 *
 * Returns true if the teardown ran immediately, false if it was deferred.
 */
export const requestSessionTeardown = (
  teardown: () => void | Promise<void>,
  /**
   * WHY this session is ending. Always logged.
   *
   * Until 2026-08-07 the credential paths tore a session down in total silence, so a
   * clinician reporting "it just logged me out" could not be told which of four mechanisms
   * did it, and neither could anyone reading the console afterwards. Diagnosis fell back to
   * guessing from timing. A session ending is the single most disruptive thing this app
   * does to a clinician mid-work; it must never be anonymous.
   */
  reason: string = 'unspecified'
): boolean => {
  if (!isEncounterAudioAtRisk()) {
    console.warn(`[sessionTeardown] Ending session — reason: ${reason}`)
    void teardown()
    return true
  }

  console.warn(
    `[sessionTeardown] Session teardown requested (reason: ${reason}) during an active ` +
      'encounter — DEFERRED. Navigating away now would unmount the recorder and destroy ' +
      'audio that exists only in this tab. The session will end once the recording is ' +
      'finished and uploaded.'
  )
  pendingReason = reason
  pendingTeardown = teardown

  // Subscribe once; the store is the only thing that can tell us the encounter ended.
  if (!unsubscribe) {
    unsubscribe = store.subscribe(() => {
      if (!pendingTeardown || isEncounterAudioAtRisk()) return
      const run = pendingTeardown
      pendingTeardown = null
      unsubscribe?.()
      unsubscribe = null
      console.warn(
        `[sessionTeardown] Encounter finished — running the deferred session teardown ` +
          `(reason: ${pendingReason}).`
      )
      void run()
    })
  }
  return false
}

/** Test seam: drop any deferred teardown and unsubscribe. */
export const __resetSessionTeardown = (): void => {
  pendingTeardown = null
  unsubscribe?.()
  unsubscribe = null
}

/** Test/diagnostic seam: is a teardown currently being held? */
export const hasPendingTeardown = (): boolean => pendingTeardown !== null
